import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-hot-toast";
import {
  FolderOpen,
  ChevronDown,
  Search,
  MessageSquare,
  Database,
  Sparkles,
  Check,
} from "lucide-react";
import documentSearchService, {
  FolderOption,
} from "../../services/document-search.service";
import Loader from "../../widgets/Loader";
import DocumentSearchTab from "../../components/documents/DocumentSearchTab";
import AIChatTab from "../../components/documents/AIChatTab";
import KnowledgeBaseTab from "../../components/documents/KnowledgeBaseTab";

type TabType = "document-search" | "ai-chat" | "knowledge-base";

const DocumentSearch: React.FC = () => {
  const [folders, setFolders] = useState<FolderOption[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>("ai-chat");

  const [mountedTabs, setMountedTabs] = useState<Set<TabType>>(
    new Set(["ai-chat"]),
  );

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchFolders();
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchFolders = async () => {
    try {
      const response = await documentSearchService.getFolderDropdown();
      if (response.success) {
        setFolders(response.data);
      } else {
        toast.error("Failed to fetch folders");
      }
    } catch (error: any) {
      console.error("Error fetching folders:", error);
      toast.error("Failed to fetch folders");
    } finally {
      setIsLoading(false);
    }
  };

  const handleFolderChange = (folderId: string) => {
    setSelectedFolderId(folderId);
    setActiveTab("ai-chat");
    setMountedTabs(new Set(["ai-chat"]));
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  const handleTabChange = (tabId: TabType) => {
    setActiveTab(tabId);
    setMountedTabs((prev) => new Set([...prev, tabId]));
  };

  const filteredFolders = folders.filter((folder) =>
    folder.folder_name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const selectedFolderObj = folders.find((f) => f.id === selectedFolderId);

  const tabs = [
    {
      id: "document-search" as TabType,
      label: "Document Search",
      icon: Search,
      activeGradient: "from-blue-600 to-cyan-600",
    },
    {
      id: "ai-chat" as TabType,
      label: "AI Chat",
      icon: MessageSquare,
      activeGradient: "from-indigo-600 to-pink-600",
    },
    {
      id: "knowledge-base" as TabType,
      label: "Knowledge Base",
      icon: Database,
      activeGradient: "from-violet-600 to-purple-600",
    },
  ];

  return (
    <div className="h-full flex flex-col bg-slate-50 overflow-hidden">
      {/* Header: Title + Folder + Tabs */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 z-40">
        <div className="px-5 py-3 flex items-center gap-5">
          {/* Title */}
          <div className="flex items-center gap-2.5 flex-shrink-0">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-600">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div className="hidden lg:block">
              <h1 className="text-sm font-bold text-slate-900 leading-tight">
                Document Search
              </h1>
              <p className="text-[10px] text-slate-500">
                Search and chat with your documents using AI
              </p>
            </div>
          </div>

          {/* Folder Dropdown — only show in header when a folder is selected */}
          {selectedFolderId && (
            <div className="w-full max-w-xs" ref={dropdownRef}>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className={`relative w-full text-left px-3 py-2 pl-9 bg-white border text-sm text-slate-900 focus:outline-none transition-all font-medium flex items-center justify-between rounded-md
                    ${
                      isDropdownOpen
                        ? "border-blue-500 ring-1 ring-blue-500"
                        : "border-slate-200 hover:border-blue-300"
                    }`}
                >
                  <span className="truncate block pr-4">
                    {selectedFolderObj?.folder_name}
                    {selectedFolderObj?.document_store_type && (
                      <span className="text-slate-400 font-normal ml-1">
                        • {selectedFolderObj.document_store_type}
                      </span>
                    )}
                  </span>
                  <div className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <FolderOpen className="h-3.5 w-3.5 text-emerald-500" />
                  </div>
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown
                      className={`h-3 w-3 text-slate-400 transition-transform duration-200 ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </div>
                </button>

                {isDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-xl">
                    <div className="p-2 border-b border-slate-100">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                        <input
                          type="text"
                          className="w-full pl-7 pr-2 py-1 text-xs border border-slate-200 rounded bg-slate-50 focus:outline-none focus:border-blue-400 placeholder:text-slate-400"
                          placeholder="Search folders..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          autoFocus
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto">
                      {filteredFolders.length === 0 ? (
                        <div className="px-3 py-3 text-center text-xs text-slate-500">
                          No folders found
                        </div>
                      ) : (
                        <ul className="py-1">
                          {filteredFolders.map((folder) => {
                            const isSelected = selectedFolderId === folder.id;
                            return (
                              <li
                                key={folder.id}
                                onClick={() => handleFolderChange(folder.id)}
                                className={`px-3 py-1.5 cursor-pointer flex items-center justify-between text-xs transition-colors
                                  ${
                                    isSelected
                                      ? "bg-blue-50 text-blue-700 font-medium"
                                      : "text-slate-700 hover:bg-slate-50"
                                  }`}
                              >
                                <span className="truncate">
                                  {folder.folder_name}
                                  {folder.document_store_type && (
                                    <span
                                      className={`ml-1 ${isSelected ? "text-blue-400" : "text-slate-400"}`}
                                    >
                                      • {folder.document_store_type}
                                    </span>
                                  )}
                                </span>
                                {isSelected && (
                                  <Check className="h-3 w-3 text-blue-600" />
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tabs - inline in header */}
          {selectedFolderId && (
            <div className="flex items-center gap-1.5 ml-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-md transition-all duration-150
                      ${
                        isActive
                          ? `bg-gradient-to-r ${tab.activeGradient} text-white`
                          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900 border border-slate-200"
                      }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Content Area - takes remaining height */}
      <div className="flex-1 overflow-hidden relative min-h-0">
        {isLoading ? (
          <div className="h-full flex items-center justify-center">
            <Loader />
          </div>
        ) : !selectedFolderId ? (
          <div className="h-full overflow-y-auto bg-slate-50 chat-scrollbar">
            <div className="py-4">
              {/* Header with search */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Select a folder to get started
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Choose a document folder to start searching with AI
                  </p>
                </div>
                <div className="relative w-64" ref={dropdownRef}>
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input
                    type="text"
                    className="w-full pl-9 pr-3 py-2 text-xs border border-slate-200 rounded-md bg-white focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100 placeholder:text-slate-400"
                    placeholder="Search folders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Folder Grid — looks like actual folders */}
              {filteredFolders.length === 0 ? (
                <div className="py-16 text-center">
                  <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-400">No folders found</p>
                </div>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                  {filteredFolders.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => handleFolderChange(folder.id)}
                      className="group flex flex-col items-center text-center p-3 bg-white border border-slate-200 rounded-md hover:border-blue-300 hover:shadow transition-all"
                    >
                      <div className="relative mb-2">
                        <div className="w-10 h-8 bg-amber-400 rounded-t-sm rounded-b-md relative group-hover:bg-amber-500 transition-colors">
                          <div className="absolute -top-1 left-0 w-5 h-2 bg-amber-400 rounded-t-sm group-hover:bg-amber-500 transition-colors"></div>
                        </div>
                      </div>
                      <p className="text-[11px] font-medium text-slate-700 group-hover:text-blue-700 truncate w-full leading-tight">
                        {folder.folder_name}
                      </p>
                      {folder.document_store_type && (
                        <p className="text-[9px] text-slate-400 mt-0.5 truncate w-full">
                          {folder.document_store_type}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Feature highlights - bottom */}
              <div className="grid grid-cols-3 gap-3 mt-6">
                <div className="flex items-center gap-2.5 p-3 rounded-md bg-blue-50/70 border border-blue-100">
                  <Search className="w-4 h-4 text-blue-500 flex-shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold text-blue-900">
                      Smart Search
                    </p>
                    <p className="text-[10px] text-blue-600/50">
                      Single query, one answer
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-md bg-indigo-50/70 border border-indigo-100">
                  <MessageSquare className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold text-indigo-900">
                      AI Chat
                    </p>
                    <p className="text-[10px] text-indigo-600/50">
                      Multi-turn conversations
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2.5 p-3 rounded-md bg-violet-50/70 border border-violet-100">
                  <Database className="w-4 h-4 text-violet-500 flex-shrink-0" />
                  <div>
                    <p className="text-[11px] font-semibold text-violet-900">
                      Knowledge Base
                    </p>
                    <p className="text-[10px] text-violet-600/50">
                      Search across all files
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="absolute inset-0">
            {/* Document Search - scrollable with padding */}
            {mountedTabs.has("document-search") && (
              <div
                className={`${activeTab === "document-search" ? "block" : "hidden"} h-full overflow-y-auto chat-scrollbar bg-slate-50`}
              >
                <div className="py-5">
                  <DocumentSearchTab documentStoreId={selectedFolderId} />
                </div>
              </div>
            )}

            {/* AI Chat - full height, no scroll wrapper (handles its own) */}
            {mountedTabs.has("ai-chat") && (
              <div
                className={`${activeTab === "ai-chat" ? "block" : "hidden"} h-full`}
              >
                <AIChatTab documentStoreId={selectedFolderId} />
              </div>
            )}

            {/* Knowledge Base - scrollable with padding */}
            {mountedTabs.has("knowledge-base") && (
              <div
                className={`${activeTab === "knowledge-base" ? "block" : "hidden"} h-full overflow-y-auto chat-scrollbar bg-slate-50`}
              >
                <div className="py-5">
                  <KnowledgeBaseTab documentStoreId={selectedFolderId} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentSearch;
